import {
  DataGrid,
  GridFooterContainer,
  GridPagination,
  type GridColDef,
  type GridPaginationModel,
  type GridSortModel,
  type GridRowSelectionModel,
  type GridRowId,
  type GridValidRowModel,
  type DataGridProps,
} from '@mui/x-data-grid';
import { Box, Paper, Stack } from '@mui/material';
import { type ReactNode } from 'react';
import { FilterBar, type FilterDef } from '@/components/FilterBar';
import { BulkActionBar } from '@/components/BulkActionBar';
import { useColumnPinning, PinColumnsButton } from './useColumnPinning';

/**
 * Row-action icon styling (semantic tinted boxes) applied directly here because
 * MUI X does not reliably consume the equivalent theme `MuiDataGrid` override.
 * Keep in sync with theme.ts MuiDataGrid / MuiTableCell so plain tables match.
 */
const ACTION_ICON_SX = {
  '& .MuiDataGrid-cell[data-field="actions"] .MuiIconButton-root': {
    width: 30, height: 30, borderRadius: '8px',
    border: '1px solid #e4e9ef', color: '#6b7683',
    transition: 'background-color 140ms, border-color 140ms, color 140ms',
    '& .MuiSvgIcon-root': { fontSize: 17 },
    '&:hover': { backgroundColor: '#eaf3fc', borderColor: '#108ae6', color: '#108ae6' },
    // Every action is a neutral grey box at rest; the semantic colour appears
    // only on hover (view/edit → blue, disable → amber, delete → red).
    '&.MuiIconButton-colorError': { color: '#6b7683', borderColor: '#e4e9ef' },
    '&.MuiIconButton-colorError:hover': { backgroundColor: '#fdecea', borderColor: '#dc5640', color: '#dc5640' },
    '&.MuiIconButton-colorSuccess': { color: '#6b7683', borderColor: '#e4e9ef' },
    '&.MuiIconButton-colorSuccess:hover': { backgroundColor: '#e7f7ee', borderColor: '#1a9c63', color: '#1a9c63' },
    '&.MuiIconButton-colorWarning': { color: '#6b7683', borderColor: '#e4e9ef' },
    '&.MuiIconButton-colorWarning:hover': { backgroundColor: '#fdf4e1', borderColor: '#c8891a', color: '#c8891a' },
    '&.Mui-disabled': { opacity: 0.4 },
  },
};

/**
 * DataTable — the ONE list-table component for the app. Wraps MUI X DataGrid
 * (community) with every shared convention baked in, so each list page just
 * supplies data + config and automatically gets: a standard toolbar
 * (search + filters + pin + count), column pinning (freeze up to 4), the
 * uniform action-column styling (from the theme), server/client pagination,
 * a bulk-selection bar, sticky header, and loading/empty/error states.
 *
 * Add a feature here once → every list page gets it. Small static/config
 * tables can stay as plain <Table> (they share the global theme for looks).
 */
export type DataTableToolbar = {
  search?: { value: string; onChange: (v: string) => void; placeholder?: string;
             /** Explicit Search button instead of search-as-you-type. */
             button?: boolean };
  filters?: FilterDef[];
  values?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
  onClearAll?: () => void;
  /** Page-specific controls, shown left of the pin button + count. */
  end?: ReactNode;
  /** e.g. "113 employees" */
  count?: ReactNode;
};

export type DataTableProps<R extends GridValidRowModel> = {
  rows: R[];
  columns: GridColDef<R>[];
  getRowId: (row: R) => GridRowId;
  loading?: boolean;
  error?: ReactNode;
  emptyMessage?: ReactNode;

  /** Enable column pinning. `storageKey` persists the choice per table. */
  pin?: { storageKey: string; defaultPinned?: string[]; max?: number };

  toolbar?: DataTableToolbar;
  bulk?: { count: number; onClear: () => void; actions: ReactNode };

  paginationMode?: 'server' | 'client';
  /**
   * Server sorting. Omit for the default client sort, which is correct only
   * when the grid holds every row — with server pagination it would reorder
   * the current page and silently claim to have sorted the whole table.
   */
  sortingMode?: 'server' | 'client';
  sortModel?: GridSortModel;
  onSortModelChange?: (m: GridSortModel) => void;
  rowCount?: number;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (m: GridPaginationModel) => void;
  pageSizeOptions?: number[];
  /** Show the total + "showing x–y" note in the footer (server pagination). */
  footerTotalLabel?: (total: number, model: GridPaginationModel) => ReactNode;

  checkboxSelection?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (m: GridRowSelectionModel) => void;

  onRowClick?: DataGridProps<R>['onRowClick'];
  onRowDoubleClick?: DataGridProps<R>['onRowDoubleClick'];
  /** `() => 'auto'` lets rows grow to fit text that wraps. */
  getRowHeight?: DataGridProps<R>['getRowHeight'];
  autoHeight?: boolean;
  /**
   * When set (e.g. "calc(100vh - 330px)"), the grid takes this fixed height and
   * scrolls its rows INTERNALLY, so the column header (and footer) stay pinned
   * while you scroll. Omit for the default auto-height (page scrolls, header
   * scrolls away with it).
   */
  viewportHeight?: string | number;
  /**
   * Fill the remaining height of the page card (flexbox), so the grid is as
   * tall as the screen allows on ANY laptop — no hardcoded pixel height — with
   * a pinned header + internal scroll. Takes precedence over viewportHeight.
   *
   * The page's root must be a flex column with a definite height. Use a
   * `max()` FLOOR so the table can't collapse on small screens (it keeps a
   * usable min height and the page scrolls instead):
   *   sx={{ display:'flex', flexDirection:'column',
   *         height:'max(560px, calc(100vh - 116px))' }}
   */
  fillHeight?: boolean;
  sx?: DataGridProps<R>['sx'];
  slots?: DataGridProps<R>['slots'];
};

export function DataTable<R extends GridValidRowModel>({
  rows, columns, getRowId, loading, error, emptyMessage,
  pin, toolbar, bulk,
  paginationMode = 'server', rowCount, paginationModel, onPaginationModelChange,
  sortingMode, sortModel, onSortModelChange,
  // The MIT DataGrid refuses a page size over 100.
  pageSizeOptions = [10, 25, 50, 100], footerTotalLabel,
  checkboxSelection, rowSelectionModel, onRowSelectionModelChange,
  onRowClick, onRowDoubleClick, getRowHeight, autoHeight = true, viewportHeight, fillHeight, sx, slots,
}: DataTableProps<R>) {
  const useAutoHeight = (fillHeight || viewportHeight) ? false : autoHeight;
  // Always call the hook (Rules of Hooks); a blank storageKey just means "off".
  const pinning = useColumnPinning<R>({
    storageKey: pin?.storageKey ?? '__off__',
    columns,
    defaultPinned: pin?.defaultPinned ?? [],
    max: pin?.max ?? 4,
  });
  const pinnable = pin
    ? columns
        .filter((c) => c.field !== 'actions' && typeof c.headerName === 'string' && c.headerName)
        .map((c) => ({ field: c.field, label: c.headerName as string }))
    : [];

  const clickable = !!(onRowClick || onRowDoubleClick);

  const mergedSlots: DataGridProps<R>['slots'] = {
    ...(emptyMessage
      ? { noRowsOverlay: () => (
          <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center', p: 4, color: 'text.secondary', fontSize: 13 }}>
            {emptyMessage}
          </Stack>
        ) }
      : {}),
    ...(footerTotalLabel
      ? { footer: () => (
          <GridFooterContainer>
            <Box sx={{ pl: 2, fontSize: 13, fontWeight: 600 }}>
              {footerTotalLabel(rowCount ?? rows.length, paginationModel ?? { page: 0, pageSize: 0 })}
            </Box>
            <GridPagination />
          </GridFooterContainer>
        ) }
      : {}),
    ...slots,
  };

  return (
    <>
      {toolbar && (
        <FilterBar
          searchPlaceholder={toolbar.search?.placeholder}
          searchButton={toolbar.search?.button}
          searchValue={toolbar.search?.value ?? ''}
          onSearchChange={toolbar.search?.onChange ?? (() => {})}
          filters={toolbar.filters ?? []}
          values={toolbar.values ?? {}}
          onFilterChange={toolbar.onFilterChange ?? (() => {})}
          onClearAll={toolbar.onClearAll ?? (() => {})}
          rightSlot={
            (toolbar.end || pin || toolbar.count) ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                {toolbar.end}
                {pin && <PinColumnsButton {...pinning.buttonProps(pinnable)} />}
                {toolbar.count}
              </Stack>
            ) : undefined
          }
        />
      )}

      {/* Grid-only pages (their own toolbar) still get the pin button — in a
          right-aligned strip above the grid. */}
      {!toolbar && pin && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <PinColumnsButton {...pinning.buttonProps(pinnable)} />
        </Box>
      )}

      {bulk && <BulkActionBar count={bulk.count} onClear={bulk.onClear} actions={bulk.actions} />}

      <Paper sx={{
        border: 1, borderColor: 'divider', width: '100%', minWidth: 0,
        ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
      }}>
        <Box
          ref={pin ? pinning.gridRef : undefined}
          sx={fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}
        >
          <DataGrid
            rows={rows}
            columns={pin ? pinning.orderedColumns : columns}
            getRowId={getRowId}
            loading={loading}
            autoHeight={useAutoHeight}
            paginationMode={paginationMode}
            rowCount={paginationMode === 'server' ? rowCount : undefined}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortingMode={sortingMode}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            pageSizeOptions={pageSizeOptions}
            checkboxSelection={checkboxSelection}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={onRowSelectionModelChange}
            onRowClick={onRowClick}
            onRowDoubleClick={onRowDoubleClick}
            getRowHeight={getRowHeight}
            disableRowSelectionOnClick
            slots={mergedSlots}
            {...(pin ? pinning.gridProps : {})}
            sx={[
              {
                border: 0,
                '& .MuiDataGrid-row:hover': { cursor: clickable ? 'pointer' : 'default' },
                // Vertically centre EVERY cell's content — renderCell columns
                // (multi-line name, action buttons) default to top-aligned block
                // cells, so without this they sit higher than the plain cells.
                '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                // Match the plain-table headers (theme MuiTableCell head is the
                // #e8f4fd accent wash). The column headers paint this CSS var, and
                // pinned headers read the same var, so the whole header row is one
                // uniform blue tint like every other table in the app.
                '--DataGrid-containerBackground': '#e8f4fd',
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, color: '#333' },
                '& .MuiDataGrid-columnHeaders': { borderBottom: '1px solid #dbe7f0' },
              },
              ACTION_ICON_SX,
              ...(fillHeight ? [{ flex: 1, minHeight: 0 }] : []),
              ...(!fillHeight && viewportHeight ? [{ height: viewportHeight }] : []),
              ...(pin ? [pinning.pinnedSx] : []),
              ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
            ]}
          />
        </Box>
        {error && <Box sx={{ p: 2, color: 'error.main', fontSize: 13 }}>{error}</Box>}
      </Paper>
    </>
  );
}
